import React, { useMemo, useState } from 'react';
import { Button, Switch, Input } from 'antd';
import { XCircleIcon } from '@heroicons/react/24/outline';
import { useTheme } from '@src/theme';

interface ManageColumnsProps {
    allColumns: { key: string; title: string }[];
    hiddenColumns: string[];
    setHiddenColumns: (keys: string[]) => void;
    onClose: () => void;
}

export const ManageColumns = ({
    allColumns,
    hiddenColumns,
    setHiddenColumns,
    onClose,
}: ManageColumnsProps) => {
    const { theme } = useTheme();
    const [searchTerm, setSearchTerm] = useState('');

    const handleSwitchChange = (colKey: string, checked: boolean) => {
        if (checked) {
            setHiddenColumns(hiddenColumns.filter((k) => k !== colKey));
        } else {
            setHiddenColumns([...hiddenColumns, colKey]);
        }
    };

    const showAll = () => setHiddenColumns([]);
    const hideAll = () => setHiddenColumns(allColumns.map((col) => col.key));

    const filteredColumns = useMemo(() => {
        if (!searchTerm) return allColumns;
        return allColumns.filter(
            (col) =>
                col.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                col.key.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [allColumns, searchTerm]);

    return (
        <div
            className="rounded-xl p-2 shadow-2xl"
            style={{
                width: 260,
                backgroundColor: theme.semantic.surface,
            }}
        >
            <div
                className="flex justify-between items-center mb-2"
                style={{ color: theme.semantic.textPrimary }}
            >
                <span className="font-medium">Manage Columns</span>
                <XCircleIcon
                    width={24}
                    height={24}
                    className="cursor-pointer"
                    onClick={onClose}
                />
            </div>

            <Input
                size="large"
                placeholder="Search columns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mb-4"
            />

            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {filteredColumns.map((col) => (
                    <div
                        key={col.key}
                        className="flex justify-between items-center mb-4 px-2"
                        style={{ color: theme.semantic.textPrimary }}
                    >
                        <span>{col.title || col.key}</span>
                        <Switch
                            checked={!hiddenColumns.includes(col.key)}
                            onChange={(checked) =>
                                handleSwitchChange(col.key, checked)
                            }
                            style={{
                                backgroundColor: !hiddenColumns.includes(
                                    col.key
                                )
                                    ? theme.semantic.primary
                                    : undefined,
                            }}
                        />
                    </div>
                ))}
            </div>
            <div className="flex justify-between gap-2 py-2 border-t-2">
                <Button
                    className="bg-transparent w-full border-none justify-start hover:!bg-transparent"
                    style={{ color: theme.semantic.textPrimary }}
                    variant="text"
                    onClick={showAll}
                >
                    Show All
                </Button>
                <Button
                    className="bg-transparent w-full border-none justify-end hover:!bg-transparent"
                    style={{ color: theme.semantic.textPrimary }}
                    variant="text"
                    onClick={hideAll}
                >
                    Hide All
                </Button>
            </div>
        </div>
    );
};
